import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Eyebrow, Btn } from "../components/Primitives";
import { api } from "../lib/api";
import logger from "../lib/logger";

const RISE_INIT = { opacity: 0, y: 6 };
const RISE_ANIM = { opacity: 1, y: 0 };

const EVENT_LABELS = {
    match_created: "New match",
    availability_submitted: "Availability",
    date_proposed: "Date proposed",
    date_confirmed: "Date confirmed",
    briefing_ready: "Pre-date briefing",
    checkin_nudge: "After your date",
    expiry_warning: "Match expiring",
};

function timeAgo(iso) {
    if (!iso) return "";
    const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return "just now";
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
}

export default function NotificationsInbox({ user, onBack, onOpenDating }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        try {
            const r = await api.listNotifications(user.session_id);
            setItems(r.items || []);
        } catch (e) {
            logger.error("inbox load failed", e);
        }
        setLoading(false);
    }, [user.session_id]);

    useEffect(() => { load(); }, [load]);

    const handleOpen = async (n) => {
        if (!n.read) {
            try { await api.markNotificationRead(user.session_id, n.id); } catch (e) { logger.warn("mark read failed", e); }
        }
        // Every dating notification drops them into the dating flow
        if (onOpenDating) onOpenDating();
    };

    const handleMarkAll = async () => {
        try {
            await api.markAllNotificationsRead(user.session_id);
            await load();
        } catch (e) { logger.error("mark-all failed", e); }
    };

    const unread = items.filter(i => !i.read).length;

    return (
        <div className="flex-1 flex flex-col bg-cream" data-testid="notifications-inbox">
            <div className="px-6 pt-6 pb-2 flex items-center justify-between">
                <button data-testid="inbox-back-btn" onClick={onBack} className="tap-target text-[11px] tracking-[0.28em] uppercase text-ink-muted">← Back</button>
                <span className="text-[10px] tracking-[0.32em] uppercase text-ink-muted">Alerts</span>
            </div>

            <div className="px-7 pt-4 pb-2">
                <Eyebrow>In-app alerts</Eyebrow>
                <h1 className="mt-2 font-display text-4xl text-burgundy leading-[1.1]">
                    <span className="italic">Your</span> updates.
                </h1>
                <p className="mt-2 text-sm text-ink-muted leading-snug">
                    Match alerts, date confirmations, and your 24-hour pre-date briefing — all in here.
                </p>
                {unread > 0 && (
                    <button
                        data-testid="inbox-mark-all"
                        onClick={handleMarkAll}
                        className="mt-3 text-[10px] tracking-[0.28em] uppercase text-burgundy underline underline-offset-4"
                    >
                        Mark all read ({unread})
                    </button>
                )}
            </div>

            <div className="flex-1 px-6 py-4 space-y-2 overflow-y-auto">
                {loading && <div className="text-ink-muted text-sm text-center py-10">Loading…</div>}

                {!loading && items.length === 0 && (
                    <div className="text-center py-12 px-4" data-testid="inbox-empty">
                        <p className="font-display italic text-xl text-burgundy">No alerts yet.</p>
                        <p className="mt-3 text-sm text-ink-muted leading-relaxed">
                            When you get matched, when a date is confirmed, and 24 hours before you meet someone — it'll all show up here.
                        </p>
                    </div>
                )}

                {!loading && items.map((n, i) => (
                    <motion.button
                        key={n.id}
                        initial={RISE_INIT}
                        animate={RISE_ANIM}
                        transition={{ delay: i * 0.04 }}
                        onClick={() => handleOpen(n)}
                        data-testid={`inbox-item-${n.id}`}
                        className={`w-full text-left p-4 border transition-colors flex items-start gap-3 ${
                            n.read
                                ? "bg-cream border-burgundy/10 hover:border-burgundy/30"
                                : "bg-white border-burgundy/40"
                        }`}
                    >
                        {!n.read && (
                            <span className="mt-1.5 w-2 h-2 rounded-full bg-burgundy shrink-0" data-testid={`unread-dot-${n.id}`} />
                        )}
                        <div className="flex-1 min-w-0">
                            <div className="flex items-baseline gap-2">
                                <span className={`font-display text-base ${n.read ? "text-burgundy/70" : "text-burgundy"}`}>
                                    {n.title}
                                </span>
                            </div>
                            <p className={`mt-0.5 text-sm leading-snug ${n.read ? "text-ink-muted" : "text-ink"}`}>
                                {n.body}
                            </p>
                            <div className="mt-1.5 flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-ink-light">
                                <span>{EVENT_LABELS[n.event] || n.event}</span>
                                <span>·</span>
                                <span>{timeAgo(n.created_at)}</span>
                            </div>
                        </div>
                    </motion.button>
                ))}
            </div>
        </div>
    );
}
